/*
 * Super Admin — Indicator Help Text Editor (Phase 4).
 *
 * Mounted at /app/help-text.
 *  • Left rail: pillar nav with "missing/draft/published" counts
 *  • Right pane: indicator list + inline expand-to-edit panels
 *  • Toolbar: "AI Seed All" button, "Publish All Drafts" button, status filter
 *
 * Backend: GET /api/admin/indicator-help[?pillar_id], /missing, PATCH, /publish.
 */
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { withTimeout } from "@/hooks/useLoadState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "@/components/ui/sonner";
import {
  Sparkles, CheckCircle2, FileText, RefreshCcw, Eye, EyeOff, ChevronDown, ChevronUp, Search, AlertCircle,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const adminHdrs = (session) => ({
  "x-tenant-id": session?.tenant?.id || "tenant_1",
  "x-user-id":   session?.user?.id   || "",
  "x-user-role": session?.user?.role || "admin",
});

const PILLARS = [
  { id: "instruction", label: "Instruction" },
  { id: "admin_leadership", label: "Administrative Leadership" },
  { id: "fiscal", label: "Fiscal" },
  { id: "governance", label: "Governance" },
  { id: "compliance", label: "Compliance" },
  { id: "student_perf", label: "Student Performance" },
  { id: "cmo_emo", label: "CMO/EMO Management" },
  { id: "mission", label: "Mission and Key Design Elements" },
];

const STATUS_PILL = {
  draft:       "text-[#a16207] bg-[#fefce8] border-[#fde047]",
  published:   "text-[#065f46] bg-[#ecfdf5] border-[#a7f3d0]",
  unpublished: "text-[#6b7280] bg-[#f3f4f6] border-[#e5e7eb]",
  missing:     "text-[#b91c1c] bg-[#fef2f2] border-[#fecaca]",
};

const HelpTextAdminPage = ({ session }) => {
  const [helpRows, setHelpRows] = useState([]);
  const [missing, setMissing] = useState({ total: 0, missing: [], unpublished_or_draft: [] });
  const [pillarId, setPillarId] = useState("instruction");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [loadErr, setLoadErr] = useState(null);
  const [seeding, setSeeding] = useState(false);

  const load = async () => {
    setLoading(true);
    setLoadErr(null);
    try {
      const [r, m] = await withTimeout(Promise.all([
        axios.get(`${API}/api/admin/indicator-help`, { headers: adminHdrs(session) }),
        axios.get(`${API}/api/admin/indicator-help/missing`, { headers: adminHdrs(session) }),
      ]));
      setHelpRows(r.data || []);
      setMissing(m.data || { total: 0, missing: [], unpublished_or_draft: [] });
    } catch (e) {
      setLoadErr(e?.response?.data?.detail || e?.message || "Failed to load help text");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const rowByIndicator = useMemo(() => {
    const m = new Map();
    helpRows.forEach((r) => m.set(r.indicator_id, r));
    return m;
  }, [helpRows]);

  // Synthesize the full indicator list per pillar by merging missing + drafts + helpRows
  const indicatorsForPillar = useMemo(() => {
    const items = [];
    const seen = new Set();
    helpRows.filter((r) => r.pillar_id === pillarId).forEach((r) => {
      items.push({ ...r, _missing: false });
      seen.add(r.indicator_id);
    });
    (missing.missing || []).filter((m) => m.pillar_id === pillarId).forEach((m) => {
      if (seen.has(m.indicator_id)) return;
      items.push({ indicator_id: m.indicator_id, indicator_name: m.indicator_name, pillar_id: m.pillar_id, status: "missing", _missing: true });
    });
    return items.sort((a, b) => (a.indicator_name || "").localeCompare(b.indicator_name || ""));
  }, [helpRows, missing, pillarId]);

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    return indicatorsForPillar.filter((r) => {
      if (statusFilter !== "all" && r.status !== statusFilter) return false;
      if (s && !(r.indicator_name || "").toLowerCase().includes(s)) return false;
      return true;
    });
  }, [indicatorsForPillar, search, statusFilter]);

  const countsByPillar = useMemo(() => {
    const out = {};
    PILLARS.forEach((p) => { out[p.id] = { total: 0, missing: 0, draft: 0, published: 0 }; });
    (missing.missing || []).forEach((m) => { if (out[m.pillar_id]) out[m.pillar_id].missing++; });
    helpRows.forEach((r) => {
      if (!out[r.pillar_id]) return;
      out[r.pillar_id][r.status]++;
    });
    PILLARS.forEach((p) => {
      out[p.id].total = (out[p.id].missing || 0) + (out[p.id].draft || 0) + (out[p.id].published || 0) + (out[p.id].unpublished || 0);
    });
    return out;
  }, [helpRows, missing]);

  const aiSeed = async () => {
    if (!window.confirm("Run GPT-4o to seed all 115 indicators with draft help text? (Existing rows are kept.)")) return;
    setSeeding(true);
    try {
      await axios.post(`${API}/api/admin/indicator-help/ai-seed`, {}, { headers: adminHdrs(session) });
      toast.success("AI seed started — refresh in a couple of minutes to see drafts populate.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setSeeding(false);
    }
  };

  const publishAll = async () => {
    if (!window.confirm("Publish ALL draft help text rows? They'll become visible to tenant users.")) return;
    try {
      const r = await axios.post(`${API}/api/admin/indicator-help/publish-all-drafts`, {}, { headers: adminHdrs(session) });
      toast.success(`Published ${r.data.published} draft(s)`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const patchRow = async (indicator_id, patch) => {
    try {
      const r = await axios.patch(`${API}/api/admin/indicator-help/${indicator_id}`, patch, { headers: adminHdrs(session) });
      setHelpRows((arr) => {
        const next = arr.filter((x) => x.indicator_id !== indicator_id);
        next.push(r.data);
        return next;
      });
      toast.success("Saved");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const publishOne = async (indicator_id) => {
    try {
      await axios.post(`${API}/api/admin/indicator-help/${indicator_id}/publish`, {}, { headers: adminHdrs(session) });
      toast.success("Published");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const unpublishOne = async (indicator_id) => {
    try {
      await axios.post(`${API}/api/admin/indicator-help/${indicator_id}/unpublish`, {}, { headers: adminHdrs(session) });
      toast.success("Unpublished");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const totalPublished = helpRows.filter((r) => r.status === "published").length;
  const totalDraft = helpRows.filter((r) => r.status === "draft").length;
  const totalMissing = (missing.missing || []).length;

  return (
    <div className="px-6 lg:px-10 py-8 max-w-[1700px] mx-auto" data-testid="help-text-admin-page">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
        <div>
          <div className="text-[11.5px] uppercase tracking-wider text-[#6b7280] font-semibold">Operations</div>
          <h1 className="text-3xl font-bold text-[#111827] flex items-center gap-2">
            <FileText className="h-7 w-7 text-[#3B4FE4]" /> Indicator Help Text
          </h1>
          <p className="text-sm text-[#6b7280] mt-1">
            {missing.total || 115} indicators · <strong className="text-[#065f46]">{totalPublished}</strong> published · <strong className="text-[#a16207]">{totalDraft}</strong> draft · <strong className="text-[#b91c1c]">{totalMissing}</strong> missing
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={aiSeed} disabled={seeding} className="bg-[#7c3aed] hover:bg-[#6d28d9] text-white" data-testid="help-ai-seed-btn">
            <Sparkles className="h-4 w-4 mr-1" /> {seeding ? "Seeding…" : "AI Seed All"}
          </Button>
          <Button onClick={publishAll} variant="outline" className="border-[#a7f3d0] text-[#065f46]" data-testid="help-publish-all-btn">
            <CheckCircle2 className="h-4 w-4 mr-1" /> Publish All Drafts
          </Button>
          <Button onClick={load} variant="outline" className="border-[#e5e7eb]">
            <RefreshCcw className="h-4 w-4 mr-1" /> Refresh
          </Button>
        </div>
      </div>

      <div className="grid lg:grid-cols-[280px_1fr] gap-6">
        {/* Pillar nav */}
        <aside className="lg:sticky lg:top-6 lg:self-start">
          <div className="rounded-xl border border-[#e5e7eb] bg-white p-2 space-y-0.5" data-testid="help-pillar-nav">
            {PILLARS.map((p) => {
              const c = countsByPillar[p.id] || {};
              const isSel = p.id === pillarId;
              return (
                <button
                  key={p.id}
                  onClick={() => setPillarId(p.id)}
                  className={`w-full text-left px-3 py-2.5 rounded-md transition-colors ${isSel ? "bg-[#0b1220] text-white" : "hover:bg-[#f9fafb]"}`}
                  data-testid={`help-pillar-${p.id}`}
                >
                  <div className={`text-[13px] font-semibold ${isSel ? "text-white" : "text-[#111827]"}`}>{p.label}</div>
                  <div className={`text-[11.5px] mt-1 flex items-center gap-2 ${isSel ? "text-white/60" : "text-[#6b7280]"}`}>
                    <span className="font-bold">{c.total || 0}</span> indicators
                    {c.missing > 0 && <span className="text-[#fecaca]">· {c.missing} missing</span>}
                    {c.draft > 0 && <span className="text-[#fde047]">· {c.draft} draft</span>}
                  </div>
                </button>
              );
            })}
          </div>
        </aside>

        {/* Right pane */}
        <section>
          <div className="flex items-center gap-2 flex-wrap mb-3">
            {["all", "published", "draft", "unpublished", "missing"].map((s) => (
              <button
                key={s}
                onClick={() => setStatusFilter(s)}
                className={`px-3 py-1.5 rounded-full text-[12px] font-semibold border transition-colors ${
                  statusFilter === s ? "bg-[#0b1220] text-white border-[#0b1220]" : "bg-white text-[#374151] border-[#e5e7eb] hover:bg-[#f9fafb]"
                }`}
                data-testid={`help-status-${s}`}
              >
                {s.charAt(0).toUpperCase() + s.slice(1)}
              </button>
            ))}
            <div className="ml-auto flex items-center gap-1 border border-[#e5e7eb] rounded-md bg-white px-2">
              <Search className="h-3.5 w-3.5 text-[#9ca3af]" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search indicators…"
                className="bg-transparent text-[13px] py-1.5 px-1 outline-none w-[280px]"
                data-testid="help-search"
              />
            </div>
          </div>

          {loadErr && !loading ? (
            <div role="alert" className="rounded-xl border border-[#fecaca] bg-[#fef2f2] p-8 text-center" data-testid="help-text-error">
              <div className="text-[15px] font-semibold text-[#991b1b]">Failed to load indicator help text</div>
              <div className="text-[13px] text-[#4b5563] mt-1">{loadErr}</div>
              <button onClick={load} className="mt-3 inline-flex items-center gap-1.5 h-9 px-4 rounded-lg border border-[#e5e7eb] bg-white text-[13px] font-semibold text-[#111827] hover:bg-[#f9fafb]" data-testid="help-text-retry">Retry</button>
            </div>
          ) : loading ? (
            <div className="py-20 text-center text-[#4b5563]" aria-busy="true">Loading indicator help text…</div>
          ) : (
            <div className="space-y-2">
              {filtered.length === 0 && (
                <div className="rounded-xl border border-[#e5e7eb] bg-white p-10 text-center text-[#9ca3af]">No indicators match this filter.</div>
              )}
              {filtered.map((r) => (
                <IndicatorHelpRow
                  key={r.indicator_id}
                  row={r}
                  onSave={(patch) => patchRow(r.indicator_id, patch)}
                  onPublish={() => publishOne(r.indicator_id)}
                  onUnpublish={() => unpublishOne(r.indicator_id)}
                />
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

const HELP_FIELDS = [
  { key: "explanation", label: "Plain-English explanation" },
  { key: "why_it_matters", label: "Why this matters" },
  { key: "where_to_find_data", label: "Where to find the data" },
  { key: "example_evidence", label: "Example evidence" },
  { key: "suggested_responsible_party", label: "Suggested responsible party" },
  { key: "common_mistake", label: "Common mistake to avoid" },
  { key: "annual_report_relevance", label: "Annual report relevance" },
];

const IndicatorHelpRow = ({ row, onSave, onPublish, onUnpublish }) => {
  const [open, setOpen] = useState(false);
  const [vals, setVals] = useState(() => Object.fromEntries(HELP_FIELDS.map((f) => [f.key, row[f.key] || ""])));
  const dirty = HELP_FIELDS.some((f) => (vals[f.key] || "") !== (row[f.key] || ""));
  const pill = STATUS_PILL[row.status] || STATUS_PILL.missing;

  return (
    <div className={`rounded-xl border ${row._missing ? "border-[#fecaca] bg-[#fef9f9]" : "border-[#e5e7eb] bg-white"}`} data-testid={`help-row-${row.indicator_id}`}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full text-left px-4 py-3 flex items-center justify-between gap-3"
      >
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <strong className="text-[14px] text-[#111827] truncate">{row.indicator_name}</strong>
            <span className={`text-[9px] font-bold uppercase px-1.5 py-0.5 rounded border ${pill}`}>{row.status || "missing"}</span>
          </div>
          <div className="text-[11.5px] text-[#6b7280] mt-0.5">{row.indicator_id}</div>
        </div>
        {open ? <ChevronUp className="h-4 w-4 text-[#6b7280]" /> : <ChevronDown className="h-4 w-4 text-[#6b7280]" />}
      </button>
      {open && (
        <div className="px-4 pb-4 space-y-3 border-t border-[#e5e7eb]">
          {row._missing && (
            <div className="rounded-md border border-[#fecaca] bg-white px-3 py-2 text-[12px] text-[#b91c1c] flex items-center gap-1.5 mt-2">
              <AlertCircle className="h-3.5 w-3.5" /> Missing — write & publish help text, or run AI Seed to generate a draft.
            </div>
          )}
          {HELP_FIELDS.map((f) => (
            <div key={f.key}>
              <label className="block text-[11.5px] uppercase tracking-wider text-[#6b7280] font-semibold mb-1">{f.label}</label>
              <Textarea
                value={vals[f.key]}
                onChange={(e) => setVals((v) => ({ ...v, [f.key]: e.target.value }))}
                className="bg-white border-[#e5e7eb] text-[13px] min-h-[60px]"
                data-testid={`help-field-${f.key}`}
              />
            </div>
          ))}
          <div className="flex items-center justify-between pt-2">
            <div className="text-[11.5px] text-[#6b7280]">{dirty ? "Unsaved changes" : "Saved"}</div>
            <div className="flex items-center gap-2">
              {row.status === "published" ? (
                <Button onClick={onUnpublish} variant="outline" size="sm" className="border-[#e5e7eb] text-[#6b7280]" data-testid="help-unpublish-btn">
                  <EyeOff className="h-3.5 w-3.5 mr-1" /> Unpublish
                </Button>
              ) : (
                <Button onClick={onPublish} size="sm" className="bg-[#059669] hover:bg-[#047857] text-white" data-testid="help-publish-btn">
                  <Eye className="h-3.5 w-3.5 mr-1" /> Publish
                </Button>
              )}
              <Button
                onClick={() => onSave(vals)}
                disabled={!dirty}
                size="sm"
                className="bg-[#3B4FE4] hover:bg-[#2d3fb8] text-white disabled:opacity-50"
                data-testid="help-save-btn"
              >
                Save Draft
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HelpTextAdminPage;
